// PhoneNumberItem.jsx
// Shared phone-number chip with a hover/tap popover offering Call / SMS /
// WhatsApp / Copy actions. Used across contact detail screens (customers,
// people, suppliers, company, events, site locations).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverBody } from "reactstrap";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { isWhatsAppActive, waMeDigits } from "../../WhatsApp/whatsappUtils";

/* cross-instance coordinator */
let ACTIVE_ANCHOR_ID = null;
let SUPPRESS_UNTIL_MS = 0;
const now = () => Date.now();

/* utils */
const normalizeForURI = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  const plus = s.startsWith("+");
  const digits = s.replace(/[^\d]/g, "");
  return plus ? `+${digits}` : digits;
};

const Chip = ({ as = "a", href, onClick, icon, children }) => {
  const Tag = as;
  return (
    <Tag
      href={href}
      onClick={onClick}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      style={{
        fontSize: 12,
        lineHeight: 1,
        padding: "6px 10px",
        borderRadius: 9999,
        border: "1px solid rgba(0,0,0,.12)",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
        background: "#fff",
      }}
    >
      <i className={icon} />
      {children}
    </Tag>
  );
};

// Change #100607 - onLogCall(number): optional. When a screen passes it the
// popover also offers "Log call" (record an inbound/outbound call by hand,
// RecordCallModal), the same as the "Add new" menu. Hidden otherwise, like
// EmailAddressItem's onCompose.
export default function PhoneNumberItem({ number, isDefault, idx, ownerId, onLogCall }) {
  const [isTouch, setIsTouch] = useState(false);
  const [open, setOpen] = useState(false);

  const closeTimer = useRef(null);
  const openTimer = useRef(null);

  const anchorId = useMemo(
    () =>
      `phone-anchor-${ownerId}-${idx}-${(number || "").replace(
        /[^\dA-Za-z]/g,
        ""
      )}`,
    [ownerId, idx, number]
  );

  const telHref = `tel:${normalizeForURI(number)}`;
  const smsHref = `sms:${normalizeForURI(number)}`;
  // Change #100326 - wa.me needs digits only, with the country code ("+44..." and "07..." both failed).
  const waHref = `https://wa.me/${waMeDigits(number)}`;
  const history = useHistory();
  const whatsAppInCrm = isWhatsAppActive();

  useEffect(() => {
    const touch =
      "ontouchstart" in window ||
      (navigator?.maxTouchPoints ?? 0) > 0 ||
      window.matchMedia?.("(hover: none)")?.matches;
    setIsTouch(!!touch);
  }, []);

  const onCopy = async (e) => {
    e?.preventDefault?.();
    try {
      await navigator.clipboard.writeText(number);
      toast.success("Copied phone to clipboard");
    } catch {
      toast.success("Copied");
    }
  };

  // open/close with grace so you can travel into the popover
  const openAfterDelay = (delay = 140) => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (
      now() < SUPPRESS_UNTIL_MS &&
      ACTIVE_ANCHOR_ID &&
      ACTIVE_ANCHOR_ID !== anchorId
    )
      return;
    openTimer.current = setTimeout(() => {
      if (
        ACTIVE_ANCHOR_ID &&
        ACTIVE_ANCHOR_ID !== anchorId &&
        now() < SUPPRESS_UNTIL_MS
      )
        return;
      if (closeTimer.current) clearTimeout(closeTimer.current);
      ACTIVE_ANCHOR_ID = anchorId;
      setOpen(true);
    }, delay);
  };

  const closeSoon = (delay = 220) => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      if (ACTIVE_ANCHOR_ID === anchorId) ACTIVE_ANCHOR_ID = null;
      setOpen(false);
      SUPPRESS_UNTIL_MS = now() + 180;
    }, delay);
  };

  const openNow = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    ACTIVE_ANCHOR_ID = anchorId;
    setOpen(true);
  };

  const anchorHandlers = isTouch
    ? {
        onClick: (e) => {
          e.preventDefault();
          open ? closeSoon(0) : openNow();
        },
      }
    : {
        onMouseEnter: () => openAfterDelay(140),
        onMouseLeave: () => closeSoon(220),
        onFocus: openNow,
        onBlur: () => closeSoon(150),
      };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        marginBottom: 8,
      }}
    >
      {isDefault && (
        <i
          className="fa-solid fa-star me-1 text-warning"
          title="Default"
          style={{ marginRight: 4 }}
        />
      )}

      <a
        id={anchorId}
        href={telHref}
        {...anchorHandlers}
        style={{
          color: "#212529",
          textDecoration: "none",
          marginLeft: 0,
        }}
      >
        {number}
      </a>

      <Popover
        isOpen={open}
        target={anchorId}
        toggle={() => (open ? closeSoon(0) : openNow())}
        placement="top"
        trigger="manual"
        fade={false}
        container="body"
        /* 👉 inline width applied directly to the popper (actual .popover) */
        popperStyle={{
          maxWidth: "none",
          width: "max-content",
          zIndex: 1085,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.12)",
        }}
        modifiers={[
          { name: "offset", options: { offset: [0, 10] } },
          { name: "preventOverflow", options: { boundary: "viewport" } },
        ]}
      >
        <PopoverBody
          onMouseEnter={() => !isTouch && openNow()}
          onMouseLeave={() => !isTouch && closeSoon(200)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap", // keeps chips on one line
            padding: "10px 12px",
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <Chip href={telHref} icon="ri-phone-line">
            Call
          </Chip>
          <Chip href={smsHref} icon="ri-message-2-line">
            SMS
          </Chip>
          {whatsAppInCrm && (
            <Chip
              as="button"
              icon="ri-whatsapp-line"
              onClick={(e) => {
                e?.preventDefault?.();
                setOpen(false);
                history.push(`/messaging-service?new=${encodeURIComponent(number || "")}`);
              }}
            >
              WhatsApp (CRM)
            </Chip>
          )}
          <Chip href={waHref} icon="ri-whatsapp-line">
            {whatsAppInCrm ? "wa.me" : "WhatsApp"}
          </Chip>
          <Chip as="button" onClick={onCopy} icon="ri-file-copy-2-line">
            Copy
          </Chip>
          {typeof onLogCall === "function" && (
            <Chip
              as="button"
              icon="ri-phone-find-line"
              onClick={(e) => {
                e?.preventDefault?.();
                setOpen(false);
                onLogCall(number);
              }}
            >
              Log call
            </Chip>
          )}
        </PopoverBody>
      </Popover>
    </div>
  );
}
